import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import '@/App.css';
import { Menu, X, Sparkles, ChevronDown } from 'lucide-react';
import { Toaster } from 'sonner';

// Import Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import LegacyModernizationPage from './pages/LegacyModernizationPage';
import HealthcarePage from './pages/HealthcarePage';
import TripTrustPage from './pages/TripTrustPage';
import ContactPage from './pages/ContactPage';

function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const closeTimeoutRef = React.useRef(null);

  const isActive = (path) => location.pathname === path;

  const handleProductClick = (path) => {
    setProductsDropdownOpen(false);
    // Small delay to allow dropdown close animation
    setTimeout(() => {
      navigate(path);
    }, 100);
  };

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setProductsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    // Add delay before closing to prevent premature closing
    closeTimeoutRef.current = setTimeout(() => {
      setProductsDropdownOpen(false);
    }, 300);
  };

  return (
    <>
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <Sparkles className="w-6 h-6" />
            <span>PrimeFields</span>
          </Link>
          
          <div className="nav-links">
            <Link
              to="/"
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`nav-link ${isActive('/about') ? 'active' : ''}`}
            >
              About
            </Link>
            <div 
              className="nav-dropdown"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`nav-link nav-link-button ${location.pathname.startsWith('/products') ? 'active' : ''}`}
                onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
              >
                Products <ChevronDown className={`w-4 h-4 ml-1 ${productsDropdownOpen ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s' }} />
              </button>
              {productsDropdownOpen && (
                <div className="dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      handleProductClick('/products/legacy-modernization');
                    }}
                  >
                    Legacy Modernization
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      handleProductClick('/products/healthcare');
                    }}
                  >
                    Healthcare Solutions
                  </button>
                  <button
                    className="dropdown-item"
                    onClick={(e) => {
                      e.preventDefault();
                      handleProductClick('/products/triptrust');
                    }}
                  >
                    TripTrust - Travel CRM
                  </button>
                </div>
              )}
            </div>
            <Link
              to="/contact"
              className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
            >
              Contact
            </Link>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <Link
              to="/"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            <div className="mobile-menu-link" style={{ fontWeight: 'bold', paddingTop: '0.5rem' }}>
              Products
            </div>
            <Link
              to="/products/legacy-modernization"
              className="mobile-menu-link"
              style={{ paddingLeft: '1.5rem' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Legacy Modernization
            </Link>
            <Link
              to="/products/healthcare"
              className="mobile-menu-link"
              style={{ paddingLeft: '1.5rem' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              Healthcare Solutions
            </Link>
            <Link
              to="/products/triptrust"
              className="mobile-menu-link"
              style={{ paddingLeft: '1.5rem' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              TripTrust - Travel CRM
            </Link>
            <Link
              to="/contact"
              className="mobile-menu-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="footer-logo">
              <Sparkles className="w-6 h-6" />
              <span>PrimeFields</span>
            </div>
            <p className="footer-tagline">Powering Digital Transformation Across Industries</p>
          </div>
          <div className="footer-links">
            <div className="footer-section">
              <h4 className="footer-heading">Products</h4>
              <Link to="/products/legacy-modernization" className="footer-link">Legacy Modernization</Link>
              <Link to="/products/healthcare" className="footer-link">Healthcare Solutions</Link>
              <Link to="/products/triptrust" className="footer-link">TripTrust CRM</Link>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Company</h4>
              <Link to="/about" className="footer-link">About Us</Link>
              <Link to="/contact" className="footer-link">Contact</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 PrimeFields. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <Router>
      <div className="App">
        <ScrollToTop />
        <Navigation />
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/products/legacy-modernization" element={<LegacyModernizationPage />} />
          <Route path="/products/healthcare" element={<HealthcarePage />} />
          <Route path="/products/triptrust" element={<TripTrustPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
